// "Make the PDF" on the Yearly Summaries tab (#515 stage 2): drawn in the browser, saved to the job's
// Proposal & Take-Off folder and opened from there in a new tab, as the Schedule of Work (lib/file-handoff.ts).
// It carries the sell figures, so it waits for the REVIEW to show the price.
import { useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import { Button, OutboundLink } from '@higgins/ui';
import type { DeviceRecord } from '../lib/device-db';
import { auToday, isoToday } from '../lib/dates';
import { openWaitingTab } from '../lib/file-handoff';
import { useOnline } from '../lib/hooks';
import { drawYearlySummaries, yearlyFileName } from '../lib/maintenance-pdf';
import type { MaintenancePricing } from '../lib/maintenance-pricing';
import { msalInstance } from '../lib/msal';
import { saveToJob } from '../lib/schedule/generate';
import { ErrorMessage } from './message';

interface Props {
  jobNumber: string;
  optionName: string;
  version: DeviceRecord;
  pricing: MaintenancePricing;
  sides: ReadonlyMap<string, string | null>;
  priceShown: boolean;
}

export function YearlyPdfPanel({ jobNumber, optionName, version, pricing, sides, priceShown }: Props) {
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string; url?: string } | null>(null);

  async function make() {
    setResult(null);
    const tab = openWaitingTab('Making the Yearly Summaries…');
    setBusy(true);
    try {
      const blob = await drawYearlySummaries({ jobNumber, optionName, pricing, sides, preparedBy: msalInstance.getActiveAccount()?.name ?? null, date: auToday() });
      const versionNumber = typeof version.fields.hig_versionnumber === 'number' ? version.fields.hig_versionnumber : null;
      const fileName = yearlyFileName(jobNumber, optionName, versionNumber, isoToday());
      const saved = await saveToJob({ fileName, blob }, version);
      if ('webUrl' in saved) {
        if (tab) tab.show(saved.webUrl);
        setResult({ ok: true, text: `${fileName} — saved to the job's Proposal & Take-Off folder and ${tab ? tab.where : 'ready to open'}.`, url: saved.webUrl });
        return;
      }
      tab?.close();
      setResult({ ok: false, text: `The Yearly Summaries weren't saved to the job: ${saved.problem} Make them again once that's sorted.` });
    } catch (err) {
      tab?.close();
      setResult({ ok: false, text: `Couldn't make the document: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setBusy(false);
    }
  }

  const blocked = !priceShown ? 'Complete the REVIEW first: the PDF shows the sell.' : !online ? "Needs signal: the PDF is saved straight to the job's SharePoint folder." : null;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button size="sm" onClick={() => void make()} disabled={busy || blocked !== null}>
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown className="mr-1 h-4 w-4" />} Make the PDF
      </Button>
      <span className="text-xs text-slate-500">{blocked ?? 'The Yearly Summaries and Scope PA, saved to the job and opened from SharePoint.'}</span>
      {result?.ok && result.url && (
        <OutboundLink openIn="edge" href={result.url} className="text-sm text-primary underline">
          {result.text}
        </OutboundLink>
      )}
      {result && !result.ok && <ErrorMessage className="w-full">{result.text}</ErrorMessage>}
    </div>
  );
}
